import React, { useState, useEffect } from 'react';
import { XMarkIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../services/api';

interface BacktestFormData {
  strategyId: string;
  symbol: string;
  startDate: string;
  endDate: string;
  initialBalance: number;
}

interface Strategy {
  id: string;
  name: string;
  type: string;
  description?: string;
}

interface TradingPairCollection {
  id: string;
  symbol: string;
  exchangeName: string;
  dataStatus: string;
  isActive: boolean;
}

interface BacktestFormProps {
  onSubmit: (data: BacktestFormData) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export const BacktestForm: React.FC<BacktestFormProps> = ({ 
  onSubmit, 
  onCancel, 
  isLoading 
}) => {
  const [formData, setFormData] = useState<BacktestFormData>({
    strategyId: '',
    symbol: '',
    startDate: '',
    endDate: '',
    initialBalance: 10000
  });

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [availablePairs, setAvailablePairs] = useState<TradingPairCollection[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [loadingPairs, setLoadingPairs] = useState(false);

  useEffect(() => {
    loadStrategies();
    loadAvailableTradingPairs();
  }, []);

  const loadStrategies = async () => {
    try {
      setLoadingStrategies(true);
      const response = await apiClient.get<{strategies: Strategy[]}>('/strategies');
      setStrategies(response.data?.strategies || []);
    } catch (error) {
      console.error('Failed to load strategies:', error);
      toast.error('Failed to load strategies');
    } finally {
      setLoadingStrategies(false);
    }
  };

  const loadAvailableTradingPairs = async () => {
    try {
      setLoadingPairs(true);
      const response = await apiClient.get<{collections: TradingPairCollection[]}>('/data-collection/trading-pairs');
      
      // Only show pairs that have up-to-date data for backtesting
      const upToDatePairs = (response.data?.collections || []).filter(
        (pair: TradingPairCollection) => pair.dataStatus === 'up_to_date' && pair.isActive
      );
      
      setAvailablePairs(upToDatePairs);
      
      console.log(`[BACKTEST_FORM] Loaded ${upToDatePairs.length} available trading pairs with historical data`);
      
    } catch (error) {
      console.error('Failed to load trading pairs:', error);
      toast.error('Failed to load trading pairs');
    } finally {
      setLoadingPairs(false);
    }
  };

  const handleInputChange = (field: keyof BacktestFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.strategyId) {
      toast.error('Please select a strategy');
      return;
    }
    
    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      toast.error('End date must be after start date');
      return;
    }
    
    if (formData.initialBalance <= 0) {
      toast.error('Initial balance must be positive');
      return;
    }

    // Check date range (max 2 years)
    const diffDays = (new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 730) {
      toast.error('Backtest period cannot exceed 2 years');
      return;
    }

    await onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Create New Backtest</h3>
          <p className="text-sm text-gray-600">Test your strategy against historical market data</p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-500"
          disabled={isLoading}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Strategy Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trading Strategy *
          </label>
          {loadingStrategies ? (
            <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading strategies...
            </div>
          ) : strategies.length > 0 ? (
            <select
              value={formData.strategyId}
              onChange={(e) => {
                handleInputChange('strategyId', e.target.value);
              }}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={isLoading}
            >
              <option value="">Select a strategy...</option>
              {strategies.map((strategy) => (
                <option key={strategy.id} value={strategy.id}>
                  {strategy.name} ({(strategy.type || 'unknown').replace('_', ' ')})
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full border rounded-lg px-3 py-2 bg-yellow-50 border-yellow-200">
              <p className="text-yellow-800 text-sm">
                No trading strategies available. 
                <br />
                <span className="font-medium">Go to the Strategies page</span> to create your first strategy.
              </p>
            </div>
          )}
        </div>

        {/* Symbol Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trading Symbol *
          </label>
          {loadingPairs ? (
            <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading trading pairs...
            </div>
          ) : availablePairs.length > 0 ? (
            <select
              value={formData.symbol}
              onChange={(e) => handleInputChange('symbol', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={isLoading}
            >
              <option value="">Select a trading pair...</option>
              {availablePairs.map((pair) => (
                <option key={pair.id} value={pair.symbol}>
                  {pair.symbol} ({pair.exchangeName.toUpperCase()})
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full border rounded-lg px-3 py-2 bg-yellow-50 border-yellow-200">
              <p className="text-yellow-800 text-sm">
                No trading pairs with historical data available. 
                <br />
                <span className="font-medium">Go to Data Collection</span> to set up data collection for trading pairs first.
              </p>
            </div>
          )}
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date *
            </label>
            <div className="relative">
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={isLoading}
                max={formData.endDate || undefined}
              />
              {/* <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" /> */}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date *
            </label>
            <div className="relative">
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={isLoading}
                min={formData.startDate || undefined}
                max={new Date().toISOString().split('T')[0]}
              />
              {/* <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" /> */}
            </div>
          </div>
        </div>

        {/* Initial Balance */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Initial Balance (USD) *
          </label>
          <div className="relative">
            <input
              type="number"
              value={formData.initialBalance}
              onChange={(e) => handleInputChange('initialBalance', parseFloat(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="100"
              max="1000000"
              step="100"
              required
              disabled={isLoading}
            />
            <CurrencyDollarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
          <p className="mt-1 text-xs text-gray-500">Minimum: $100, Maximum: $1,000,000</p>
        </div>

        {/* Advanced Configuration Toggle */}
        {/* Removed advanced config UI */}

        {/* Strategy Configuration */}
        {/* Removed strategy config UI */}

        {/* Form Status Messages */}
        {(strategies.length === 0 || availablePairs.length === 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-yellow-600">
                <svg className="h-5 w-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">Setup Required</h4>
                <div className="mt-1 text-sm text-yellow-700">
                  {strategies.length === 0 && (
                    <p>• Create at least one trading strategy to run backtests</p>
                  )}
                  {availablePairs.length === 0 && (
                    <p>• Set up data collection for trading pairs to get historical data</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.strategyId || !formData.symbol || availablePairs.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Running Backtest...
              </>
            ) : (
              <>
                {/* <CogIcon className="h-4 w-4 mr-2" /> */}
                Run Backtest
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BacktestForm; 